import React, { useState, useMemo } from 'react';
import { Card } from '../ui/card';
import { ScrollArea } from '../ui/scroll-area';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Checkbox } from '../ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { useDebounce } from '../../hooks/useDebounce';

/**
 * Left sidebar with advanced facet groups
 * Adapts options based on current scope (collection/category/global)
 */
export const FacetSidebar = ({
  // Filter options with counts
  mountingTypes = [],
  categories = [],
  families = [],
  
  // Selected state
  selectedMounting,
  selectedCategory,
  selectedFamilies,
  
  // Handlers
  onMountingChange,
  onCategoryChange,
  onFamilyToggle,
  
  // Scope context
  scopeType = 'global',
  currentCollection = '',
  disabled = false,
  fillHeight = false
}) => {
  const [familySearch, setFamilySearch] = useState('');
  const debouncedFamilySearch = useDebounce(familySearch, 200);
  const [showAllFamilies, setShowAllFamilies] = useState(false);
  const FAMILY_INITIAL_LIMIT = 20;

  const filteredFamilies = useMemo(() =>
    families.filter(f => {
      const familyName = typeof f === 'string' ? f : (f.name || f.slug || '');
      return familyName.toLowerCase().includes(debouncedFamilySearch.toLowerCase());
    }),
    [families, debouncedFamilySearch]
  );

  // Show limited or all families — virtualization via "show more"
  const visibleFamilies = useMemo(() => {
    if (showAllFamilies || debouncedFamilySearch || filteredFamilies.length <= FAMILY_INITIAL_LIMIT) {
      return filteredFamilies;
    }
    return filteredFamilies.slice(0, FAMILY_INITIAL_LIMIT);
  }, [filteredFamilies, showAllFamilies, debouncedFamilySearch]);

  return (
    <Card className={`p-4 ${fillHeight ? 'h-full min-h-0 overflow-hidden' : ''}`} data-testid="facet-sidebar">
      <ScrollArea data-testid="facet-scroll-area" className={fillHeight ? 'h-full' : 'h-[calc(100vh-280px)]'}>
        <div className="pr-4">
          <Accordion 
            type="multiple" 
            defaultValue={['mounting', 'category', 'collection', 'family']}
            className="w-full"
          >
            {/* Conditionally show CATEGORY or MOUNTING TYPE based on collection */}
            {categories.length > 0 ? (
              // Collections with diverse products: Show CATEGORIES first, then mounting
              <>
                <AccordionItem value="category" data-testid="facet-section-category">
                  <AccordionTrigger className="text-sm font-bold uppercase tracking-wide text-foreground hover:text-[hsl(var(--accent))] transition-colors">
                    <div className="flex items-center gap-2">
                      <span>
                        {currentCollection === 'lumi-tronix' ? 'DRIVER TYPE' : 
                         currentCollection === 'lumi-sys' ? 'COMPONENT TYPE' : 
                         currentCollection === 'lumi-pro' ? 'PRODUCT TYPE' :
                         currentCollection === 'lumi-indoor' ? 'LIGHT TYPE' :
                         currentCollection === 'lumi-outdoor' ? 'OUTDOOR TYPE' :
                         currentCollection === 'lumi-flex' ? 'FLEX TYPE' :
                         currentCollection === 'lumi-arc' ? 'FIXTURE TYPE' :
                         'CATEGORY'}
                      </span>
                      {selectedCategory && <Badge variant="secondary" className="text-xs bg-[hsl(var(--accent))] text-white">{1}</Badge>}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <RadioGroup 
                      value={selectedCategory} 
                      onValueChange={onCategoryChange}
                      disabled={disabled}
                      className="space-y-1"
                    >
                      <div className="flex items-center space-x-2 py-2 px-2 rounded-md hover:bg-muted/50 transition-colors">
                        <RadioGroupItem value="" id="category-all" aria-label="All product types" className="border-[hsl(var(--accent))]" />
                        <Label htmlFor="category-all" className="flex-1 cursor-pointer text-sm font-medium">
                          All {currentCollection === 'lumi-tronix' ? 'Types' : 
                               currentCollection === 'lumi-indoor' ? 'Lights' :
                               currentCollection === 'lumi-outdoor' ? 'Outdoor Lights' :
                               'Types'}
                        </Label>
                      </div>
                      {categories.map((cat) => {
                        const catSlug = typeof cat === 'string' ? cat : (cat.slug || cat.name?.toLowerCase().replace(/\s+/g, '-'));
                        const catName = typeof cat === 'string' ? cat : (cat.name || cat.slug);
                        const catCount = typeof cat === 'object' ? cat.product_count : undefined;
                        const isDisabled = disabled || catCount === 0;
                        
                        return (
                          <div 
                            key={catSlug} 
                            className={`flex items-center justify-between space-x-2 py-2 px-2 rounded-md hover:bg-muted/50 transition-colors ${
                              isDisabled ? 'opacity-40' : ''
                            }`}
                            data-testid={`facet-option-category-${catSlug}`}
                          >
                            <div className="flex items-center space-x-2 flex-1">
                              <RadioGroupItem 
                                value={catSlug} 
                                id={`cat-${catSlug}`}
                                aria-label={`Filter by ${catName}`}
                                disabled={isDisabled}
                                className="border-[hsl(var(--accent))] data-[state=checked]:bg-[hsl(var(--accent))] data-[state=checked]:border-[hsl(var(--accent))]"
                              />
                              <Label 
                                htmlFor={`cat-${catSlug}`} 
                                className="cursor-pointer text-sm flex-1"
                              >
                                {catName}
                              </Label>
                            </div>
                            {catCount !== undefined && (
                              <Badge 
                                variant="outline" 
                                className="text-xs font-mono tabular-nums text-[hsl(var(--accent))] border-[hsl(var(--accent))/30"
                              >
                                {catCount}
                              </Badge>
                            )}
                          </div>
                        );
                      })}
                    </RadioGroup>
                  </AccordionContent>
                </AccordionItem>
                
                {/* Also show mounting for these collections if available */}
                {mountingTypes.length > 0 && mountingTypes.some(m => (typeof m === 'object' ? m.product_count : 0) > 0) && (
                  <AccordionItem value="mounting" data-testid="facet-section-mounting">
                    <AccordionTrigger className="text-sm font-bold uppercase tracking-wide text-foreground hover:text-[hsl(var(--accent))] transition-colors">
                      <div className="flex items-center gap-2">
                        <span>MOUNTING TYPE</span>
                        {selectedMounting && <Badge variant="secondary" className="text-xs bg-[hsl(var(--accent))] text-white">{1}</Badge>}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                <RadioGroup 
                  value={selectedMounting} 
                  onValueChange={onMountingChange}
                  disabled={disabled}
                  className="space-y-1"
                >
                  <div className="flex items-center space-x-2 py-2 px-2 rounded-md hover:bg-muted/50 transition-colors">
                    <RadioGroupItem value="" id="mounting-all" aria-label="All mounting types" className="border-[hsl(var(--accent))]" />
                    <Label htmlFor="mounting-all" className="flex-1 cursor-pointer text-sm font-medium">
                      All Types
                    </Label>
                  </div>
                  {mountingTypes.map((mt) => {
                    const isDisabled = disabled || mt.product_count === 0;
                    return (
                      <div 
                        key={mt.slug} 
                        className={`flex items-center justify-between space-x-2 py-2 px-2 rounded-md hover:bg-muted/50 transition-colors ${
                          isDisabled ? 'opacity-40' : ''
                        }`}
                        data-testid={`facet-option-mounting-${mt.slug}`}
                      >
                        <div className="flex items-center space-x-2 flex-1">
                          <RadioGroupItem 
                            value={mt.slug} 
                            id={`mt-${mt.slug}`}
                            aria-label={`Filter by ${mt.name || mt.slug} mounting`}
                            disabled={isDisabled}
                            className="border-[hsl(var(--accent))] data-[state=checked]:bg-[hsl(var(--accent))] data-[state=checked]:border-[hsl(var(--accent))]"
                          />
                          <Label 
                            htmlFor={`mt-${mt.slug}`} 
                            className="cursor-pointer text-sm flex-1"
                          >
                            {mt.name}
                          </Label>
                        </div>
                        <Badge 
                          variant="outline" 
                          className="text-xs font-mono tabular-nums text-[hsl(var(--accent))] border-[hsl(var(--accent))/30"
                        >
                          {mt.product_count !== undefined ? mt.product_count : '?'}
                        </Badge>
                      </div>
                    );
                  })}
                </RadioGroup>
              </AccordionContent>
            </AccordionItem>
                )}
              </>
            ) : (
              // Other collections: Show MOUNTING TYPE as primary
              <AccordionItem value="mounting" data-testid="facet-section-mounting">
                <AccordionTrigger className="text-sm font-bold uppercase tracking-wide text-foreground hover:text-[hsl(var(--accent))] transition-colors">
                  <div className="flex items-center gap-2">
                    <span>MOUNTING TYPE</span>
                    {selectedMounting && <Badge variant="secondary" className="text-xs bg-[hsl(var(--accent))] text-white">{1}</Badge>}
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <RadioGroup 
                    value={selectedMounting} 
                    onValueChange={onMountingChange}
                    disabled={disabled}
                    className="space-y-1"
                  >
                    <div className="flex items-center space-x-2 py-2 px-2 rounded-md hover:bg-muted/50 transition-colors">
                      <RadioGroupItem value="" id="mounting-all-2" className="border-[hsl(var(--accent))]" />
                      <Label htmlFor="mounting-all-2" className="flex-1 cursor-pointer text-sm font-medium">
                        All Types
                      </Label>
                    </div>
                    {mountingTypes.map((mt) => {
                      const isDisabled = disabled || mt.product_count === 0;
                      return (
                        <div 
                          key={mt.slug} 
                          className={`flex items-center justify-between space-x-2 py-2 px-2 rounded-md hover:bg-muted/50 transition-colors ${
                            isDisabled ? 'opacity-40' : ''
                          }`}
                          data-testid={`facet-option-mounting-${mt.slug}`}
                        >
                          <div className="flex items-center space-x-2 flex-1">
                            <RadioGroupItem 
                              value={mt.slug} 
                              id={`mt-2-${mt.slug}`}
                              disabled={isDisabled}
                              className="border-[hsl(var(--accent))] data-[state=checked]:bg-[hsl(var(--accent))] data-[state=checked]:border-[hsl(var(--accent))]"
                            />
                            <Label 
                              htmlFor={`mt-2-${mt.slug}`} 
                              className="cursor-pointer text-sm flex-1"
                            >
                              {mt.name}
                            </Label>
                          </div>
                          <Badge 
                            variant="outline" 
                            className="text-xs font-mono tabular-nums text-[hsl(var(--accent))] border-[hsl(var(--accent))/30"
                          >
                            {mt.product_count !== undefined ? mt.product_count : '?'}
                          </Badge>
                        </div>
                      );
                    })}
                  </RadioGroup>
                </AccordionContent>
              </AccordionItem>
            )}

            {/* Family/Series */}
            {families.length > 0 && (
              <AccordionItem value="family" data-testid="facet-section-family">
                <AccordionTrigger className="text-sm font-bold uppercase tracking-wide text-foreground hover:text-[hsl(var(--accent))] transition-colors">
                  <div className="flex items-center gap-2">
                    <span>FAMILY / SERIES</span>
                    {selectedFamilies.length > 0 && (
                      <Badge variant="secondary" className="text-xs bg-[hsl(var(--accent))] text-white">
                        {selectedFamilies.length}
                      </Badge>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  {families.length > 10 && (
                    <Input
                      placeholder="Search families..."
                      value={familySearch}
                      onChange={(e) => setFamilySearch(e.target.value)}
                      className="mb-3 h-8 text-sm border-[hsl(var(--accent))/30 focus:border-[hsl(var(--accent))]"
                      data-testid="facet-family-search-input"
                    />
                  )}
                  <ScrollArea className="h-[240px]">
                    <div className="space-y-1">
                      {visibleFamilies.map((family) => {
                        const familyName = typeof family === 'string' ? family : family.name;
                        const familyValue = typeof family === 'string' ? family : family.slug;
                        const familyCount = typeof family === 'object' && family.count ? family.count : null;
                        const isChecked = selectedFamilies.includes(familyValue);
                        
                        return (
                          <div 
                            key={familyName}
                            className="flex items-center space-x-2 py-2 px-2 rounded-md hover:bg-muted/50 transition-colors"
                            data-testid={`facet-option-family-${familyName.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <Checkbox
                              id={`fam-${familyName}`}
                              checked={isChecked}
                              onCheckedChange={(checked) => onFamilyToggle(familyValue, checked)}
                              disabled={disabled}
                              className="border-[hsl(var(--accent))] data-[state=checked]:bg-[hsl(var(--accent))] data-[state=checked]:border-[hsl(var(--accent))]"
                            />
                            <Label 
                              htmlFor={`fam-${familyName}`} 
                              className="cursor-pointer text-sm flex-1 flex items-center justify-between font-medium"
                            >
                              <span>{familyName}</span>
                              {familyCount && (
                                <span className="text-xs text-[hsl(var(--accent))] ml-2">({familyCount})</span>
                              )}
                            </Label>
                          </div>
                        );
                      })}
                      {/* Show More / Show Less */}
                      {!debouncedFamilySearch && filteredFamilies.length > FAMILY_INITIAL_LIMIT && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full text-xs text-muted-foreground hover:text-[hsl(var(--accent))] mt-1"
                          onClick={() => setShowAllFamilies(!showAllFamilies)}
                          data-testid="facet-family-show-more"
                        >
                          {showAllFamilies 
                            ? 'Show Less' 
                            : `Show All (${filteredFamilies.length - FAMILY_INITIAL_LIMIT} more)`
                          }
                        </Button>
                      )}
                      {filteredFamilies.length === 0 && (
                        <p className="text-sm text-muted-foreground py-4 text-center">
                          No families match "{familySearch}"
                        </p>
                      )}
                    </div>
                  </ScrollArea>
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>
        </div>
      </ScrollArea>
    </Card>
  );
};
